// ─── Entry ──────────────────────────────────────────────────────────────────
import './styles.css';
import { ONBOARDING_SEED, todayDateString } from '@game/seed';
import type { GameResult, ShareReceipt } from '@gametypes';
import { initAnalytics, track, bumpSession } from './analytics';
import {
  fetchDailyBoard,
  fetchLeaderboard,
  submitScore,
  drainScoreQueue,
  getPersonalBest,
  savePersonalBest,
  fetchUnlockedSkins,
  startCheckout,
} from './api';
import { AudioEngine } from './audio';
import { Game } from './engine/game';
import { UI } from './ui';
import { buildReceipt, shareReceipt } from './share';
import { WEB_SKINS, BUNDLE_ENTITLEMENT_ID, BUNDLE_SKIN_IDS, findWebSkin } from './skins';

// ── streak (local, additive — never punishes a missed day) ──────────────────
function bumpStreak(today: string): number {
  try {
    const raw = JSON.parse(localStorage.getItem('sb_streak') ?? '{}') as {
      last?: string;
      count?: number;
    };
    if (raw.last === today) return raw.count ?? 1;
    const y = new Date(Date.parse(today) - 86400000).toISOString().slice(0, 10);
    const count = raw.last === y ? (raw.count ?? 0) + 1 : 1;
    localStorage.setItem('sb_streak', JSON.stringify({ last: today, count }));
    return count;
  } catch {
    return 1;
  }
}
function currentStreak(): number {
  try {
    const raw = JSON.parse(localStorage.getItem('sb_streak') ?? '{}') as { count?: number };
    return raw.count ?? 0;
  } catch {
    return 0;
  }
}
const firstVisit = (): boolean => {
  try {
    if (localStorage.getItem('sb_onboarded')) return false;
    localStorage.setItem('sb_onboarded', '1');
    return true;
  } catch {
    return false;
  }
};

// ── skins (cosmetic IAP — docs/spec.md §4 "2026-09-17 — Web IAP override") ──
function loadSkinId(): string {
  try {
    return localStorage.getItem('sb_skin') ?? 'puzzle-rush';
  } catch {
    return 'puzzle-rush';
  }
}
function saveSkinId(id: string): void {
  try {
    localStorage.setItem('sb_skin', id);
  } catch {
    /* ignore */
  }
}

// ── viewport height ────────────────────────────────────────────────────────
// #app is `height: 100svh` (the smallest viewport — all browser UI shown), the
// one value guaranteed to fit above iOS's toolbars. This only *shrinks* it
// further if visualViewport reports even less is actually visible (keyboard,
// or a browser that svh doesn't fully cover). It never grows #app past svh.
function syncAppHeight(): void {
  const app = document.getElementById('app');
  const vv = window.visualViewport;
  if (!app || !vv) return;
  app.style.height = ''; // reset to the CSS 100svh
  const svh = app.clientHeight;
  if (vv.height < svh - 1) app.style.height = `${Math.round(vv.height)}px`;
}
syncAppHeight();
window.visualViewport?.addEventListener('resize', syncAppHeight);
window.addEventListener('orientationchange', () => window.setTimeout(syncAppHeight, 200));

// ?debug — on-screen viewport readout for diagnosing device-specific clipping
if (location.search.includes('debug')) {
  const d = document.createElement('div');
  d.style.cssText =
    'position:fixed;left:4px;bottom:4px;z-index:9999;background:#000c;color:#0f9;' +
    'font:11px/1.4 monospace;padding:6px 8px;border-radius:6px;white-space:pre;pointer-events:none';
  document.body.appendChild(d);
  const tick = (): void => {
    const vv = window.visualViewport;
    const app = document.getElementById('app');
    const wrap = document.querySelector('.board-wrap') as HTMLElement | null;
    const cv = document.querySelector('.board') as HTMLCanvasElement | null;
    const r = cv?.getBoundingClientRect();
    d.textContent =
      `inner    ${window.innerWidth}x${window.innerHeight}\n` +
      `visualVP ${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : '—'}\n` +
      `#app     ${app?.clientWidth ?? '—'}x${app?.clientHeight ?? '—'}\n` +
      `wrap     ${wrap?.clientWidth ?? '—'}x${wrap?.clientHeight ?? '—'}\n` +
      `canvas r ${r ? `${Math.round(r.width)}x${Math.round(r.height)}` : '—'}\n` +
      `canvas a ${cv ? `${cv.width}x${cv.height}` : '—'}\n` +
      `dpr      ${window.devicePixelRatio}`;
    requestAnimationFrame(tick);
  };
  tick();
}

// ── boot ───────────────────────────────────────────────────────────────────
const mount = document.getElementById('app')!;

initAnalytics();
track({ event: 'app_opened', props: { session_number: bumpSession(), source: 'load' } });

// Return-to-foreground counts as a fresh session, but debounce tab-flipping:
// only re-fire app_opened after ≥ 30 min hidden.
let lastOpen = Date.now();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  if (Date.now() - lastOpen < 30 * 60 * 1000) return;
  lastOpen = Date.now();
  track({ event: 'app_opened', props: { session_number: currentSession(), source: 'foreground' } });
});
function currentSession(): number {
  try {
    return Number(localStorage.getItem('sb_session') ?? '1');
  } catch {
    return 1;
  }
}
void drainScoreQueue();

const date = todayDateString();
let seed = date;
let themeName = 'Daily Challenge';
let personalBest = getPersonalBest(date);
let onboarding = false;
let lastReceipt: ShareReceipt | null = null;
// Starts on the free skin, never a cached premium one — a saved premium
// selection is only applied once refreshEntitlements() has verified it's
// still owned (e.g. not refunded since the last visit). See main.ts boot.
let currentSkinId = 'puzzle-rush';
let unlockedSkinIds: string[] = [];
let bundleOwned = false;

const audio = new AudioEngine();

function applySkin(id: string): void {
  const skin = findWebSkin(id);
  currentSkinId = skin.id;
  audio.setTrack(skin.url);
  document.documentElement.style.setProperty('--accent', skin.accentColor);
}

/**
 * Verifies ownership before applying a saved skin — boot starts on the free
 * skin (always safe) and only switches to a premium one here, once it's
 * confirmed. Never trust the localStorage id alone: an entitlement can be
 * revoked (refund) after it was last selected.
 */
async function refreshEntitlements(): Promise<void> {
  unlockedSkinIds = await fetchUnlockedSkins();
  bundleOwned = BUNDLE_SKIN_IDS.every((id) => unlockedSkinIds.includes(id));
  const saved = findWebSkin(loadSkinId());
  if (saved.free || unlockedSkinIds.includes(saved.id)) {
    applySkin(saved.id);
  } else {
    saveSkinId('puzzle-rush');
  }
}

function renderSkinsModal(): void {
  ui.showSkins(WEB_SKINS, unlockedSkinIds, currentSkinId, bundleOwned, {
    onSelect: (id) => {
      applySkin(id);
      saveSkinId(id);
      track({ event: 'skin_changed', props: { skin: id } });
      renderSkinsModal();
    },
    onBuy: (entitlementId) => void buy(entitlementId),
    onBuyBundle: () => void buy(BUNDLE_ENTITLEMENT_ID),
  });
}

async function buy(entitlementId: string): Promise<void> {
  track({ event: 'iap_initiated', props: { entitlement: entitlementId } });
  const err = await startCheckout(entitlementId);
  // startCheckout only returns non-null on failure — success navigates away.
  if (err) ui.setSkinsNote(err);
}

/**
 * Stripe redirects back the instant Checkout completes, which can beat the
 * webhook's delivery (fulfillment is async — Stripe doesn't guarantee it
 * lands before the redirect). Poll briefly instead of checking once, so a
 * few hundred ms of webhook lag doesn't look like a silent failure.
 */
async function waitForEntitlement(entitlement: string): Promise<void> {
  const skinId = WEB_SKINS.find((s) => s.entitlementId === entitlement)?.id;
  for (let attempt = 0; attempt < 8; attempt++) {
    await refreshEntitlements();
    const got =
      entitlement === BUNDLE_ENTITLEMENT_ID
        ? bundleOwned
        : skinId != null && unlockedSkinIds.includes(skinId);
    if (got) {
      renderSkinsModal();
      ui.setSkinsNote('Purchase complete — enjoy!');
      return;
    }
    renderSkinsModal();
    ui.setSkinsNote('Confirming your purchase…');
    await new Promise((r) => setTimeout(r, 1000));
  }
  ui.setSkinsNote(
    "Payment went through, but it's taking longer than expected to confirm — reopen Skins in a moment.",
  );
}

function handleCheckoutReturn(): void {
  const params = new URLSearchParams(location.search);
  const status = params.get('checkout');
  if (status === 'success') {
    const entitlement = params.get('skin') ?? '';
    track({ event: 'iap_completed', props: { entitlement } });
    renderSkinsModal();
    void waitForEntitlement(entitlement);
  }
  if (status) {
    const url = new URL(location.href);
    url.searchParams.delete('checkout');
    url.searchParams.delete('skin');
    history.replaceState({}, '', url.toString());
  }
}

const ui = new UI(mount, {
  onPlayDaily: () => startGame(),
  onPlayAgain: () => startGame(),
  onPause: () => {
    game.pause();
    ui.showPaused();
  },
  onResume: () => {
    game.resume();
    ui.hidePaused();
  },
  onQuit: () => {
    game.stop();
    ui.showLanding();
  },
  onShare: () => void doShare(),
  onOpenLeaderboard: () => void openLeaderboard(),
  onOpenSkins: () => renderSkinsModal(),
});

const game = new Game(ui.canvas, audio, {
  onTokenPlaced: (lane) => track({ event: 'token_placed', props: { lane } }),
  onChainCleared: (c, combo, wasOnBeat) =>
    track({
      event: 'chain_cleared',
      props: { color: c.color, combo, length: c.length, was_on_beat: wasOnBeat },
    }),
  onBeatBonus: (combo) => track({ event: 'beat_sync_bonus', props: { combo } }),
  onMaxCombo: (maxCombo) => track({ event: 'max_combo_reached', props: { max_combo: maxCombo } }),
  onOnboardingChain: (moves) => track({ event: 'onboarding_chain_fired', props: { moves } }),
  onScoreChanged: (score, combo) => {
    ui.setScore(score, combo);
    if (!onboarding) ui.setPersonalBest(score - personalBest);
  },
  onGameOver: () => handleGameOver(),
});

ui.setStreak(currentStreak());
ui.showLanding();

// Starts on the free skin (already the default above); refreshEntitlements()
// switches to the saved skin once its ownership is verified, so a player
// can never play a scored game on a premium BPM before that check resolves.
void refreshEntitlements();
handleCheckoutReturn();

// board fetch is non-blocking — the game is playable on the local seed immediately
void fetchDailyBoard().then((b) => {
  seed = b.seed;
  themeName = b.themeName;
  ui.setTheme(themeName);
});

// ── actions ────────────────────────────────────────────────────────────────
function startGame(): void {
  onboarding = firstVisit();
  const s = onboarding ? ONBOARDING_SEED : seed;
  void audio.start();
  game.start(s, findWebSkin(currentSkinId).bpm);
  ui.showPlaying();
  track({ event: 'game_started', props: { mode: onboarding ? 'onboarding' : 'daily' } });
}

function handleGameOver(): void {
  const e = game.engine;
  const beatPct = e.chainsCleared > 0 ? Math.round((e.onBeatCount / e.chainsCleared) * 100) : 0;
  const isPb = !onboarding && e.score > personalBest;

  track({
    event: 'game_over',
    props: {
      score: e.score,
      chains_cleared: e.chainsCleared,
      max_combo: e.maxCombo,
      on_beat_pct: beatPct,
      total_moves: e.totalMoves,
      is_pb: isPb,
      mode: onboarding ? 'onboarding' : 'daily',
    },
  });
  track({ event: 'game_over_screen_viewed', props: {} });

  const result: GameResult = {
    score: e.score,
    chainsCleared: e.chainsCleared,
    onBeatCount: e.onBeatCount,
    maxCombo: e.maxCombo,
    totalMoves: e.totalMoves,
    moves: e.moves,
    challengeDate: onboarding ? 'onboarding' : date,
  };
  lastReceipt = buildReceipt(result, onboarding ? 'Tutorial' : themeName);

  if (!onboarding) {
    if (isPb) {
      personalBest = e.score;
      savePersonalBest(date, e.score);
    }
    void submitScore({
      challengeDate: date,
      score: e.score,
      chainsCleared: e.chainsCleared,
      onBeatCount: e.onBeatCount,
      maxCombo: e.maxCombo,
      totalMoves: e.totalMoves,
      moves: e.moves,
    });
    const streak = bumpStreak(date);
    ui.setStreak(streak);
    if ([3, 7, 14, 30, 50, 100].includes(streak)) {
      track({ event: 'streak_milestone_reached', props: { streak } });
    }
  }

  audio.pause();
  ui.showGameOver({
    score: e.score,
    chains: e.chainsCleared,
    beatPct,
    maxCombo: e.maxCombo,
    isPb,
    onboarding,
  });
}

async function doShare(): Promise<void> {
  if (!lastReceipt) return;
  const outcome = await shareReceipt(lastReceipt, findWebSkin(currentSkinId).accentColor);
  const method = outcome === 'copied' ? 'clipboard' : 'native';
  track({ event: 'share_card_tapped', props: { method } });
  if (outcome === 'shared' || outcome === 'copied') {
    track({ event: 'share_completed', props: { method } });
    ui.setShareNote(outcome === 'copied' ? 'Copied — paste it anywhere.' : 'Thanks for sharing!');
  } else if (outcome === 'failed') {
    ui.setShareNote('Could not share — try a screenshot.');
  }
}

async function openLeaderboard(): Promise<void> {
  track({ event: 'leaderboard_viewed', props: {} });
  const entries = await fetchLeaderboard(date);
  const mine = onboarding ? null : game.engine.score || personalBest || null;
  ui.showLeaderboard(entries, mine, { themeName, date });
}
